import React from 'react';
import AsyncSelect from 'react-select/async';
import { components, DropdownIndicatorProps, ValueContainerProps, GroupBase } from 'react-select';
import { MdKeyboardArrowRight } from 'react-icons/md';

import './Dropdown.scss';
import { UsePaginationMutation } from '@services/index';
import { ApiPaginationResponse } from '@services/providers/cosem.types';

export type DropdownOption = {
    id: string;
    label: string;
};

export type AsyncDropdownProps<T> = {
    onOptionChange: (option: DropdownOption | DropdownOption[] | null) => void;
    value?: DropdownOption | DropdownOption[];
    menuIsOpen?: boolean;
    required?: boolean;
    label?: string;
    disabled?: boolean;
    mutationHook: UsePaginationMutation<T>;
    isSearchable?: boolean;
    name?: string;
    mapDataToOptions: (data: T[]) => DropdownOption[];
    menuPlacement?: 'auto' | 'bottom' | 'top';
    isMulti?: boolean;
};

const DropdownIndicator = (
    props: DropdownIndicatorProps<DropdownOption, false, GroupBase<DropdownOption>>
) => {
    return (
        <components.DropdownIndicator {...props}>
            <MdKeyboardArrowRight fontSize={28} />
        </components.DropdownIndicator>
    );
};

const ValueContainer = ({
    children,
    ...props
}: ValueContainerProps<DropdownOption, false, GroupBase<DropdownOption>>) => {
    return (
        <components.ValueContainer {...props}>
            <div className="dropdown__label">
                {props.selectProps.placeholder}
            </div>
            {children}
        </components.ValueContainer>
    );
};

export const AsyncDropdown = <T,>({
    onOptionChange,
    label,
    value,
    mutationHook,
    menuIsOpen,
    disabled = false,
    isSearchable = true,
    name,
    required = false,
    mapDataToOptions,
    menuPlacement = 'auto',
    isMulti = false
}: AsyncDropdownProps<T>) => {
    const [getData] = mutationHook();

    const loadOptions = async (inputValue: string) => {
        try {
            const result = await getData({ search: inputValue }) as { data: ApiPaginationResponse<T> };
            return mapDataToOptions(result.data.data.results);
        } catch (error) {
            console.error('Error loading options:', error);
            return [];
        }
    };


    return (
        <AsyncSelect<DropdownOption, boolean>
            name={name}
            required={required}
            className="dropdown"
            classNamePrefix="dropdown"
            isClearable
            isMulti={isMulti}
            components={{
                DropdownIndicator,
                ValueContainer
            }}
            placeholder={label + (required ? ' *' : '')}
            onChange={(option) => onOptionChange(option as DropdownOption | DropdownOption[] | null)}
            menuIsOpen={menuIsOpen}
            value={value}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.label}
            loadOptions={loadOptions}
            defaultOptions
            isSearchable={isSearchable}
            isDisabled={disabled}
            menuPlacement={menuPlacement}
        />
    );
};
